import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import axios from "axios";
import PropTypes from "prop-types";

export default function MarksEntryDialog({
  open,
  onClose,
  courseRow,
  component,
  clientId,
  collegeId,
}) {
  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState({});
  const [maxMarks, setMaxMarks] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState("");
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = JSON.parse(sessionStorage.getItem("user"));

  //const collegeId = user?.college_id;
  /* =========================
     LOAD STUDENTS
     ========================= */
  /* useEffect(() => {
    if (!open || !courseRow?.student_rollnos?.length) return;

     const fetchStudents = async () => {
      setLoading(true);
      try {
        const res = await axios.post(`${API_BASE_URL}/api/students/by-rollnos`, {
          rollnos: courseRow.student_rollnos,
        });
        setStudents(res.data.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [open, courseRow]);
 */
  useEffect(() => {
    if (!open || !courseRow) return;

    // 🔥 FULL RESET
    setStudents([]);
    setMarks({});
    setFieldErrors({});
    setValidationError("");

    const initial = {};
    courseRow.student_rollnos?.forEach((r) => {
      initial[r] = "";
    });
    setMaxMarks(component.max_marks);
    setMarks(initial);
  }, [open, courseRow, component]);

  useEffect(() => {
    if (!open || !courseRow) return;

    const fetchExistingMarks = async () => {
      try {
        const res = await axios.post(
          `${API_BASE_URL}/api/internal-marks/existing/${clientId}/${collegeId}/${component.component_id}/existing`,
          {
            courses: [
              {
                exam_course_id: courseRow.exam_course_id,
                year_number: courseRow.year_number,
                semester_number: courseRow.semester_number,
                student_rollnos: courseRow.student_rollnos,
              },
            ],
          }
        );

        const courseData = res.data?.data?.[0];
        if (!courseData?.records) return;

        const loadedMarks = {};
        const errors = {};

        courseData.records.forEach((row) => {
          loadedMarks[row.student_rollno] = row.marks === -1 ? "AB" : String(row.marks);
          errors[row.student_rollno] = "";
        });

        // 🔁 Merge with existing marks state
        //setMarks((prev) => ({ ...prev, ...loadedMarks }));
        //setFieldErrors((prev) => ({ ...prev, ...errors }));
        setMarks(loadedMarks);
        setFieldErrors(errors);
      } catch (err) {
        console.error("Failed to load existing marks", err);
      }
    };

    fetchExistingMarks();
  }, [open, courseRow]);

  /* =========================
     RESET MARKS ON OPEN
     ========================= */
  useEffect(() => {
    if (open && courseRow?.student_rollnos) {
      const initial = {};
      courseRow.student_rollnos.forEach((r) => {
        initial[r] = "";
      });
      setMarks(initial);
    }
  }, [open, courseRow]);

  /* =========================
     HANDLE MARK CHANGE
     ========================= */
  const handleMarkChange = (rollno, value) => {
    //const max = Number(courseRow.internal_maximum);
    //const max = Number(component.max_marks);
    const max =
      component?.component_name === "INTERNAL"
        ? Number(courseRow?.internal_maximum)
        : Number(component?.max_marks);
    const val = value.toUpperCase();

    // Allow empty while typing
    if (val === "") {
      setMarks((p) => ({ ...p, [rollno]: "" }));
      setFieldErrors((p) => ({ ...p, [rollno]: "Required" }));
      return;
    }

    // Allow partial "A" while typing
    if (val === "A") {
      setMarks((p) => ({ ...p, [rollno]: "A" }));
      setFieldErrors((p) => ({ ...p, [rollno]: "" }));
      return;
    }

    // Allow full AB
    if (val === "AB") {
      setMarks((p) => ({ ...p, [rollno]: "AB" }));
      setFieldErrors((p) => ({ ...p, [rollno]: "" }));
      return;
    }

    // Numbers only
    if (!/^\d+$/.test(val)) {
      setFieldErrors((p) => ({
        ...p,
        [rollno]: "Only AB or numbers",
      }));
      return;
    }

    const num = Number(val);
    if (num < 0 || num > max) {
      setFieldErrors((p) => ({
        ...p,
        [rollno]: `0 - ${max} only`,
      }));
      return;
    }

    setMarks((p) => ({ ...p, [rollno]: val }));
    setFieldErrors((p) => ({ ...p, [rollno]: "" }));
  };

  const validateAllMarks = () => {
    if (!courseRow?.student_rollnos?.length) return false;

    const min = 0;
    const max =
      component?.component_name === "INTERNAL"
        ? Number(courseRow?.internal_maximum)
        : Number(component?.max_marks);

    for (const rollno of courseRow.student_rollnos) {
      const value = marks[rollno];

      // Empty
      if (value === "" || value === undefined || value === null) {
        setValidationError(`Please enter marks for Roll No ${rollno}`);
        return false;
      }

      // Allow AB
      if (String(value).toUpperCase() === "AB") {
        continue;
      }

      // Prevent partial A
      if (String(value).toUpperCase() === "A") {
        setValidationError(`Incomplete mark (AB) for Roll No ${rollno}`);
        return false;
      }

      const num = Number(value);

      // Non-numeric
      if (isNaN(num)) {
        setValidationError(`Invalid marks for Roll No ${rollno}`);
        return false;
      }

      // Range check
      if (num < min || num > max) {
        setValidationError(`Marks for Roll No ${rollno} must be between ${min} and ${max}`);
        return false;
      }
    }

    setValidationError("");
    return true;
  };

  const handleFacultyConfirm = async () => {
    if (!validateAllMarks()) return;
    const payload = courseRow.student_rollnos.map((rollno) => ({
      exam_course_id: Number(courseRow.exam_course_id),
      year_number: Number(courseRow.year_number),
      semester_number: Number(courseRow.semester_number),
      course_code: courseRow.course_code,
      component_id: Number(component.component_id),
      student_rollno: rollno,
      marks: /^AB$/i.test(marks[rollno]) ? -1 : Number(marks[rollno]),
      fac_confirm: 1,
      hod_confirm: 0,
      coe_confirm: 0,
      created_by: user?.user_email,
      modified_by: user?.user_email,
    }));

    await axios.post(`${API_BASE_URL}/api/internal-marks/${clientId}/${collegeId}/save`, {
      marks: payload,
    });

    alert("Marks saved successfully ✅");
    onClose();
  };
  const absentCount = Object.values(marks).filter((v) => String(v).toUpperCase() === "AB").length;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Marks Entry — {courseRow?.course_code || ""} -{courseRow?.course_name || ""} (
        {component?.component_name || ""})
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <CircularProgress />
        ) : (
          <Box>
            {/* ================= HEADER ================= */}
            <Box
              sx={{
                borderBottom: "1px solid #e0e0e0",
                pb: 1,
                mb: 1,
                fontWeight: 600,
              }}
            >
              <Typography sx={{ mt: 1, fontWeight: 600 }}>
                Maximum Marks:{" "}
                {component?.component_name === "INTERNAL" ? courseRow?.internal_maximum : maxMarks}{" "}
                Absent Students: {absentCount}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={2}>
                  S.No
                </Grid>
                <Grid item xs={5}>
                  Roll No
                </Grid>

                <Grid item xs={3}>
                  Marks
                </Grid>
              </Grid>
            </Box>

            {/* ================= ROWS ================= */}
            {courseRow?.student_rollnos?.map((rollno, index) => {
              const student = students.find((s) => String(s.rollno) === String(rollno));

              return (
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  key={rollno}
                  sx={{
                    py: 1,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <Grid item xs={2}>
                    {index + 1}
                  </Grid>

                  <Grid item xs={5}>
                    {rollno}
                  </Grid>

                  <Grid item xs={3}>
                    <TextField
                      size="small"
                      value={marks[rollno] ?? ""}
                      onChange={(e) => handleMarkChange(rollno, e.target.value.trim())}
                      error={Boolean(fieldErrors[rollno])}
                      helperText={fieldErrors[rollno] || ""}
                      onBlur={() => {
                        if (marks[rollno] === "A") {
                          setMarks((p) => ({ ...p, [rollno]: "AB" }));
                          setFieldErrors((p) => ({ ...p, [rollno]: "" }));
                        }
                      }}
                      inputProps={{
                        style: {
                          textAlign: "center",
                          fontSize: "18px", // 🔥 increase input font size
                          fontWeight: 400, // optional (bolder)
                        },
                      }}
                      sx={{ width: 100 }}
                    />
                  </Grid>
                </Grid>
              );
            })}
          </Box>
        )}
      </DialogContent>
      {validationError && (
        <Box sx={{ color: "error.main", px: 3, pb: 1 }}>⚠️ {validationError}</Box>
      )}
      <DialogActions>
        <Button
          variant="contained"
          color="success"
          onClick={handleFacultyConfirm}
          disabled={loading}
        >
          Faculty Confirm
        </Button>

        <Button disabled variant="outlined">
          HOD Confirm
        </Button>

        <Button disabled variant="outlined">
          COE Confirm
        </Button>

        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

MarksEntryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  courseRow: PropTypes.object,
  component: PropTypes.object,
  clientId: PropTypes.number,
  collegeId: PropTypes.number,
};
